import { useQueries, useQuery } from '@tanstack/react-query';
import { chunk, isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  CatalogClient,
  CatalogResults,
  Feature,
  OnSaleEventQuery,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const useOnSaleEvents = (
  query: OnSaleEventQuery,
  disable = false,
  eventOnly = false
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { searchText, ...rest } = query;

  const { trackError } = useClientLogContext();

  const hasEventDiscoveryCapacityAndPrimaryFeature = useUserHasFeature(
    Feature.EventDiscoveryCapacityAndPrimary
  );
  const hasHideEventDiscoveryDemandDataFeature = useUserHasFeature(
    Feature.HideEventDiscoveryDemandData
  );

  const shouldQueryForSearch =
    !disable &&
    activeAccountWebClientConfig.activeAccountId != null &&
    searchText != null;
  const searchEventsQuery = useQuery({
    queryKey: [
      'searchEvents',
      activeAccountWebClientConfig.activeAccountId,
      searchText,
      disable,
    ],
    queryFn: () => {
      if (!shouldQueryForSearch) {
        return null;
      }
      return new CatalogClient(activeAccountWebClientConfig).searchOnSaleEvents(
        query
      );
    },
    enabled: shouldQueryForSearch,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.searchOnSaleEvents', error, {
          searchText,
        });
      },
    },
  });

  const shouldQueryOnSaleEvents =
    !disable &&
    activeAccountWebClientConfig.activeAccountId != null &&
    !shouldQueryForSearch &&
    rest != null;

  const onSaleEventsQuery = useQuery({
    queryKey: [
      'getCatalogForOnSaleEvents',
      activeAccountWebClientConfig.activeAccountId,
      JSON.stringify(rest),
      disable,
    ],
    queryFn: () => {
      if (!shouldQueryOnSaleEvents) {
        return null;
      }
      return new CatalogClient(
        activeAccountWebClientConfig
      ).getCatalogForOnSaleEvents(query!);
    },

    enabled: shouldQueryOnSaleEvents,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getCatalogForOnSaleEvents', error, {
          query,
        });
      },
    },
  });

  const isLoading = useMemo(
    () => onSaleEventsQuery.isLoading || searchEventsQuery.isLoading,
    [onSaleEventsQuery.isLoading, searchEventsQuery.isLoading]
  );

  const onSaleEvents = useMemo((): CatalogResults | null => {
    const events = searchEventsQuery.data ?? onSaleEventsQuery.data ?? null;
    for (const key in events?.events ?? {}) {
      if (isNaN(parseInt(key))) {
        delete events?.events[key];
      }
    }
    return events;
  }, [onSaleEventsQuery.data, searchEventsQuery.data]);

  const onSaleEventIds = useMemo(
    () =>
      Object.keys(onSaleEvents?.events ?? {})
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id)),
    [onSaleEvents]
  );

  const shouldAggregateQuery =
    !disable &&
    !eventOnly &&
    onSaleEventIds.length > 0 &&
    !!activeAccountWebClientConfig.activeAccountId &&
    hasHideEventDiscoveryDemandDataFeature;

  const metricsQuery = useQuery({
    queryKey: [
      'getCatalogOnSaleEventMetrics',
      activeAccountWebClientConfig.activeAccountId,
      onSaleEventIds,
      disable,
      eventOnly,
    ],
    queryFn: async () => {
      if (!shouldAggregateQuery) {
        return null;
      }

      const input: OnSaleEventQuery = {
        ...query,
        eventIds: Array.from(onSaleEventIds),
      };
      return await new CatalogClient(
        activeAccountWebClientConfig
      ).getCatalogOnSaleEventMetrics(input);
    },

    enabled: shouldAggregateQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getCatalogOnSaleEventMetrics', error, {
          eventIds: onSaleEventIds,
        });
      },
    },
  });

  const queries = useMemo(() => {
    const client = new CatalogClient(activeAccountWebClientConfig);

    return {
      queries: chunk(onSaleEventIds, 1000).map((idGroup) => {
        const shouldQuery =
          !disable &&
          !eventOnly &&
          idGroup.length > 0 &&
          !!activeAccountWebClientConfig.activeAccountId &&
          !hasHideEventDiscoveryDemandDataFeature;

        return {
          queryKey: [
            'getCatalogOnSaleEventMetrics',
            activeAccountWebClientConfig.activeAccountId,
            JSON.stringify(idGroup),
            disable,
            eventOnly,
          ],
          queryFn: async () => {
            if (!shouldQuery) {
              return null;
            }

            const input: OnSaleEventQuery = {
              ...query,
              eventIds: Array.from(idGroup),
            };

            return await client.getCatalogOnSaleEventMetrics(input);
          },
          enabled: shouldQuery,
          refetchOnWindowFocus: false,
          onError: (error: ErrorTypes) => {
            trackError('CatalogClient.getCatalogOnSaleEventMetrics', error, {
              eventIds: idGroup,
            });
          },
        };
      }),
    };
  }, [
    activeAccountWebClientConfig,
    onSaleEventIds,
    disable,
    eventOnly,
    hasHideEventDiscoveryDemandDataFeature,
    query,
    trackError,
  ]);

  const metricsQueriesFromHook = useQueries(queries);

  const shouldQueryEventAttributes =
    !disable &&
    !eventOnly &&
    hasEventDiscoveryCapacityAndPrimaryFeature &&
    activeAccountWebClientConfig.activeAccountId != null &&
    !isEmpty(onSaleEventIds);

  const eventAttributesQuery = useQuery({
    queryKey: [
      'getEventAttributes',
      activeAccountWebClientConfig.activeAccountId,
      onSaleEventIds,
      eventOnly,
      hasEventDiscoveryCapacityAndPrimaryFeature,
      disable,
    ],
    queryFn: () => {
      if (!shouldQueryEventAttributes) {
        return null;
      }
      return new CatalogClient(activeAccountWebClientConfig).getEventAttributes(
        onSaleEventIds
      );
    },
    enabled: shouldQueryEventAttributes,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getEventAttributes', error, {
          onSaleEventIds,
        });
      },
    },
  });

  const onSaleEventMetrics = useMemo(() => {
    if (hasHideEventDiscoveryDemandDataFeature) {
      return metricsQuery.data ?? [];
    }
    return metricsQueriesFromHook.flatMap((q) => q.data ?? []);
  }, [
    hasHideEventDiscoveryDemandDataFeature,
    metricsQuery.data,
    metricsQueriesFromHook,
  ]);

  return {
    onSaleEvents,
    onSaleEventMetrics,
    eventAttributes: eventAttributesQuery.data,
    isLoading,
  };
};
